/**
 * Chat profanity filter — Polish + English
 *
 * Replaces matched words with asterisks of identical length.
 * Uses word-boundary matching so partial words aren't caught.
 * Handles common letter substitutions (l33t-speak basics).
 */

// ── Word lists ───────────────────────────────────────────────────────────────
// Keep lowercase. The matcher is case-insensitive.

const PL_WORDS = [
  'baran', 'cholera', 'chuj', 'chuja', 'chuje', 'chujek', 'chujem', 'chujnia', 
  'chujowo', 'chujowy', 'chuju', 'ciota', 'cipa', 'cipka', 'ciul', 'debil', 
  'debilka', 'debilu', 'do cholery', 'dojebany', 'dojebać', 'dupa', 'dupe', 
  'dupek', 'dupo', 'dupsko', 'dupą', 'dziwka', 'dziwkarz', 'dziwki', 'dziwko', 
  'fiut', 'fiuta', 'fiutem', 'gnoj', 'gnój', 'gowniak', 'gowno', 'gówniak', 
  'gówniarz', 'gówno', 'idioci', 'idiota', 'idiotka', 'jasna cholera', 
  'jasny gwint', 'jebac', 'jebać', 'jebała', 'jebał', 'jebana', 'jebane', 
  'jebani', 'jebany', 'jebnie', 'jebnij', 'jebnąć', 'jebnięta', 'jebnięty', 
  'jebut', 'kretyn', 'kretyni', 'kretynka', 'kurewski', 'kurewsko', 'kurwa', 
  'kurwe', 'kurwo', 'kurwy', 'kurwą', 'kurwę', 'kurwiszcze', 'kurwiszon', 
  'kutas', 'kutasie', 'kutasy', 'ladacznica', 'matoł', 'matkojebca', 
  'najebana', 'najebany', 'najebać', 'napierdala', 'napierdalaj', 'napierdalać', 
  'obsrany', 'odjebany', 'odjebać', 'odpierdalaj', 'odpierdol', 'odpierdol się', 
  'opierdala', 'opierdalać', 'osioł', 'palant', 'patałach', 'pedal', 'pedał', 
  'pierdol', 'pierdole', 'pierdoli', 'pierdolisz', 'pierdolić', 'pierdolnij', 
  'pierdolnąć', 'pierdolnięta', 'pierdolnięty', 'pierdolona', 'pierdolone', 
  'pierdolony', 'pierdolę', 'pizda', 'pizde', 'pizdę', 'pizdy', 'podjebać', 
  'podpierdala', 'pojeb', 'pojebana', 'pojebane', 'pojebany', 'pojebie', 
  'popierdolony', 'przejebany', 'przejebać', 'przypierdala', 'przypierdalać', 
  'psia krew', 'rozjebany', 'rozjebać', 'rozpierdol', 'ruchać', 'skurwiel', 
  'skurwić', 'skurwysyn', 'skurwysyny', 'spierdalać', 'spierdalaj', 
  'spierdolić', 'spierdolił', 'spierdolony', 'srac', 'srać', 'sraka', 
  'sranie', 'suka', 'suki', 'suko', 'szmata', 'świnia', 'ujebany', 'ujebać', 
  'wkurwiać', 'wkurwiony', 'wpierdol', 'wyjebana', 'wyjebane', 'wyjebany', 
  'wyjebać', 'wypierdalaj', 'wypierdalać', 'zajebany', 'zajebiscie', 
  'zajebisty', 'zajebać', 'zajebiście', 'zapierdala', 'zapierdalaj', 
  'zapierdalać', 'zasrana', 'zasrane', 'zasraniec', 'zasrany', 'zjeb', 
  'zjebany', 'zjebać'
];

const EN_WORDS = [
  'fuck', 'fucker', 'fuckers', 'fucking', 'fucked', 'fucks', 'motherfucker', 'motherfuckers',
  'shit', 'shitty', 'shithead', 'shitheads', 'bullshit',
  'ass', 'asshole', 'assholes', 'arse', 'arsehole',
  'bitch', 'bitches', 'bitchy', 'bitching',
  'damn', 'damned', 'dammit', 'goddamn', 'goddamnit',
  'dick', 'dickhead', 'dicks',
  'cock', 'cocks', 'cocksucker',
  'cunt', 'cunts',
  'bastard', 'bastards',
  'whore', 'whores',
  'slut', 'sluts',
  'piss', 'pissed', 'pissing',
  'retard', 'retarded', 'retards',
  'faggot', 'faggots', 'fag', 'fags',
  'nigger', 'niggers', 'nigga', 'niggas',
  'twat', 'twats',
  'wanker', 'wankers',
  'tosser',
  'bollocks',
  'prick', 'pricks',
];

// ── Build a single regex ─────────────────────────────────────────────────────

// Escape special regex characters in word
const escapeRe = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Build pattern: match each word at word boundaries, case-insensitive
const allWords = [...PL_WORDS, ...EN_WORDS];
// Sort longer words first so "motherfucker" matches before "fuck"
allWords.sort((a, b) => b.length - a.length);

const pattern = new RegExp(
  '\\b(' + allWords.map(escapeRe).join('|') + ')\\b',
  'gi'
);

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Replace profanity in `text` with asterisks.
 * Returns cleaned string.
 */
export const filterProfanity = (text) => {
  if (!text) return text;
  return text.replace(pattern, (match) => '*'.repeat(match.length));
};

/**
 * Check if text contains any profanity.
 */
export const containsProfanity = (text) => {
  if (!text) return false;
  pattern.lastIndex = 0; // reset regex state
  return pattern.test(text);
};
